import palette from "../../../common/styles/palette";
import { AiAnalysis, AiFacetOrigin } from "../../../services/sheai/SheAiChecklistTypes";

// Extração compartilhada por Soft Skills e Dossiê; a consulta não usa os
// placeholders das facetas auditáveis do Dossiê para construir evidências.
export interface FacetasPontoAnalise {
  facial: AiFacetOrigin & {
    expressao: string;
    contatoVisual: string;
    valencia: "positiva" | "neutra" | "atencao";
  };
  corporal: AiFacetOrigin & {
    postura: string;
    gestos: string;
    ergonomia: string;
  };
  softSkillAudio: AiFacetOrigin & {
    tomVoz: string;
    escutaAtiva: string;
    comunicacao: string;
  };
  hardSkillVideo: AiFacetOrigin & {
    itemReferencia?: string;
    tecnicaMedica: string;
    aderenciaChecklist: "conforme" | "parcial" | "nao_conforme";
  };
}

export interface SoftSkillsData {
  radarSentimentos: {
    atributo: string;
    score: number;
    referencia: number;
  }[];
  tempoFala: {
    participante: string;
    segundos: number;
    label: string;
    cor: string;
  }[];
  participacaoFala: {
    id: string;
    label: string;
    value: number;
    color: string;
  }[];
  timelineFala: {
    id: number;
    speaker: "estudante" | "paciente" | "avaliador";
    start: number;
    end: number;
    texto: string;
    emocao?: string;
    facetas: FacetasPontoAnalise;
  }[];
  tempoTotal: number;
  tempoEstudante: number;
  tempoPaciente: number;
  pctEstudante: number;
  pctPaciente: number;
  nuvemPalavras: {
    termo: string;
    peso: number;
    categoria: "clinica" | "comunicacao" | "atitude";
  }[];
  pareceres: {
    emocoes: {
      titulo: string;
      resumo: string;
      destaques: string[];
      notaAspecto: string;
    };
    postura: {
      titulo: string;
      resumo: string;
      padroes: string[];
      notaAspecto: string;
    };
    comunicacao: {
      titulo: string;
      resumo: string;
      indicadores: string[];
      notaAspecto: string;
    };
    parecerGlobal: {
      titulo: string;
      resumo: string;
      pontosFortes: string[];
      oportunidades: string[];
      sintese: string;
    };
  } | null;
}

export interface SoftSkillsSttResult {
  speech_possession?: string;
  wordcloud?: string;
  segments_trancriptions?: Array<{
    speaker: string;
    start: number;
    end: number;
    transcription: string;
  }>;
}

/** Converte MM:SS ou número para segundos inteiros */
const parseTimestampToSeconds = (ts?: string | number | null): number => {
  if (!ts) return 0;
  if (typeof ts === "number") return Math.round(ts);
  const partes = String(ts).trim().split(":");
  if (partes.length === 2) {
    return parseInt(partes[0], 10) * 60 + parseInt(partes[1], 10);
  }
  if (partes.length === 3) {
    return parseInt(partes[0], 10) * 3600 + parseInt(partes[1], 10) * 60 + parseInt(partes[2], 10);
  }
  const floatVal = parseFloat(String(ts));
  return isNaN(floatVal) ? 0 : Math.round(floatVal);
};

/** Formata segundos em MM:SS */
export const formatarMinutosSegundos = (segundosTotais: number): string => {
  const min = Math.floor(segundosTotais / 60);
  const sec = Math.floor(segundosTotais % 60);
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

// O transcript puro nunca herda conteúdo clínico do cenário/checklist. Facetas
// só podem ser preenchidas por uma saída multimodal real; na ausência dela,
// explicitamos indisponibilidade em vez de fabricar observações.
const FACETAS_NAO_DISPONIVEIS: FacetasPontoAnalise = {
  facial: {
    expressao: "Não avaliado neste segmento de áudio",
    contatoVisual: "Não disponível no transcript",
    valencia: "neutra",
  },
  corporal: {
    postura: "Não avaliada neste segmento de áudio",
    gestos: "Não disponíveis no transcript",
    ergonomia: "Não disponível no transcript",
  },
  softSkillAudio: {
    tomVoz: "Não avaliado",
    escutaAtiva: "Não avaliada",
    comunicacao: "Transcript literal sem inferência",
  },
  hardSkillVideo: {
    tecnicaMedica: "Não avaliada pelo transcript",
    aderenciaChecklist: "parcial",
  },
};


const facetasDoMomentoAdk = (
  momento?: NonNullable<AiAnalysis["timeline_multimodal"]>[number]
): FacetasPontoAnalise => {
  const facets = momento?.facets as any;
  if (!facets) return FACETAS_NAO_DISPONIVEIS;
  const facial = facets.facial || {};
  const body = facets.body || {};
  const audio = facets.audio || {};
  const hard = facets.hard_skill || {};
  return {
    facial: {
      ...origemDaFaceta(facial),
      expressao: facial.expression || "Não observada nesta fala",
      contatoVisual: facial.eye_contact_pct != null
        ? `Contato visual estimado em ${facial.eye_contact_pct}%`
        : "Não mensurável nesta fala",
      valencia: facial.valence === "positiva" || facial.valence === "atencao" ? facial.valence : "neutra",
    },
    corporal: {
      ...origemDaFaceta(body),
      postura: body.posture || "Não observada nesta fala",
      gestos: body.gestures || "Não observados nesta fala",
      ergonomia: body.ergonomics || "Não mensurável nesta fala",
    },
    softSkillAudio: {
      ...origemDaFaceta(audio),
      tomVoz: audio.vocal_tone || "Não mensurável nesta fala",
      escutaAtiva: audio.active_listening || "Não mensurável nesta fala",
      comunicacao: audio.communication || "Não mensurável nesta fala",
    },
    hardSkillVideo: {
      ...origemDaFaceta(hard),
      itemReferencia: hard.item_ref,
      tecnicaMedica: hard.technique || "Não correlacionada a um item",
      aderenciaChecklist: hard.status === "done"
        ? "conforme"
        : hard.status === "done_wrong" || hard.status === "not_done"
        ? "nao_conforme"
        : "parcial",
    },
  };
};

const origemDaFaceta = (faceta: AiFacetOrigin): AiFacetOrigin => ({
  source_id: faceta.source_id,
  start: faceta.start,
  end: faceta.end,
  observation_id: faceta.observation_id,
  evidence: faceta.evidence,
  limitation: faceta.limitation,
});

/** Extrai dados e as 4 facetas multimodais integradas para cada ponto da análise */

export const extrairDadosSoftSkills = (
  analise?: AiAnalysis | null,
  sttResult?: SoftSkillsSttResult | null
): SoftSkillsData => {
  let timelineFala: SoftSkillsData["timelineFala"] = [];
  let tempoEstudante = 0;
  let tempoPaciente = 0;
  let tempoAvaliador = 0;

  // 1. Extração da Transcrição e Diarização de Áudio (Palavras Exatas do Gemini 3.8 / ADK)
  const segmentsFonte = (analise?.transcript && analise.transcript.length > 0)
    ? analise.transcript.map((t, idx) => ({
        speaker: t.speaker || "estudante",
        start: parseTimestampToSeconds(t.ts_start || t.start),
        end: parseTimestampToSeconds(t.ts_end || t.end),
        transcription: t.text || t.transcription || "",
      }))
    : sttResult?.segments_trancriptions;

  if (segmentsFonte && segmentsFonte.length > 0) {
    segmentsFonte.forEach((seg, index) => {
      const start = parseTimestampToSeconds(seg.start);
      const end = Math.max(start + 1, parseTimestampToSeconds(seg.end));
      const duracao = end - start;
      const spk = (seg.speaker || "").toLowerCase();
      let speakerType: "estudante" | "paciente" | "avaliador" = "estudante";

      if (spk.includes("0") || spk.includes("aluno") || spk.includes("estudante")) {
        tempoEstudante += duracao;
        speakerType = "estudante";
      } else if (spk.includes("1") || spk.includes("paciente") || spk.includes("ator")) {
        tempoPaciente += duracao;
        speakerType = "paciente";
      } else {
        tempoAvaliador += duracao;
        speakerType = "avaliador";
      }

      const momentoAdk = analise?.timeline_multimodal?.find(
        (momento) => Math.abs(Number(momento.start) - start) < 0.75
          && Math.abs(Number(momento.end) - end) < 1.25
      );
      timelineFala.push({
        id: index + 1,
        speaker: speakerType,
        start,
        end,
        texto: seg.transcription || "",
        emocao: undefined,
        facetas: facetasDoMomentoAdk(momentoAdk),
      });
    });
  }

  // Ordena cronologicamente por início da fala
  timelineFala.sort((a, b) => a.start - b.start);

  const tempoTotal = timelineFala.reduce((max, segmento) => Math.max(max, segmento.end), 0);
  const tempoComParticipantes = tempoEstudante + tempoPaciente;
  const pctEstudante = tempoComParticipantes > 0
    ? Math.round((tempoEstudante / tempoComParticipantes) * 100)
    : 0;
  const pctPaciente = tempoComParticipantes > 0 ? Math.max(0, 100 - pctEstudante) : 0;

  const tempoFala = [
    {
      participante: "Estudante",
      segundos: tempoEstudante,
      label: formatarMinutosSegundos(tempoEstudante),
      cor: palette.brand,
    },
    {
      participante: "Paciente / Ator",
      segundos: tempoPaciente,
      label: formatarMinutosSegundos(tempoPaciente),
      cor: palette.lilacMid,
    },
    {
      participante: "Avaliador / Outros",
      segundos: tempoAvaliador,
      label: formatarMinutosSegundos(tempoAvaliador),
      cor: "#A0AEC0",
    },
  ];

  const participacaoFala = [
    {
      id: "Estudante",
      label: "Estudante",
      value: pctEstudante,
      color: palette.brand,
    },
    {
      id: "Paciente",
      label: "Paciente",
      value: pctPaciente,
      color: palette.lilacMid,
    },
  ];

  // Scores, nuvem e pareceres vêm exclusivamente do Graph Synthesis Agent.
  const agentSoftSkills = analise?.soft_skills;
  const radarSentimentos: SoftSkillsData["radarSentimentos"] = (agentSoftSkills?.radar || []).map((p) => ({
    atributo: p.attribute,
    score: p.score,
    referencia: p.reference,
  }));
  const nuvemPalavras: SoftSkillsData["nuvemPalavras"] = (agentSoftSkills?.wordcloud || []).map((p) => ({
    termo: p.term,
    peso: p.weight,
    categoria: p.category,
  }));

  const assessments = agentSoftSkills?.assessments;
  const pareceres: SoftSkillsData["pareceres"] = assessments ? {
    emocoes: {
      titulo: assessments.emotions.title,
      resumo: assessments.emotions.summary,
      destaques: assessments.emotions.highlights || [],
      notaAspecto: assessments.emotions.score_label,
    },
    postura: {
      titulo: assessments.posture.title,
      resumo: assessments.posture.summary,
      padroes: assessments.posture.patterns || [],
      notaAspecto: assessments.posture.score_label,
    },
    comunicacao: {
      titulo: assessments.communication.title,
      resumo: assessments.communication.summary,
      indicadores: assessments.communication.indicators || [],
      notaAspecto: assessments.communication.score_label,
    },
    parecerGlobal: {
      titulo: assessments.global.title,
      resumo: assessments.global.summary,
      pontosFortes: assessments.global.strengths,
      oportunidades: assessments.global.opportunities,
      sintese: assessments.global.synthesis,
    },
  } : null;

  return {
    radarSentimentos,
    tempoFala,
    participacaoFala,
    timelineFala,
    tempoTotal,
    tempoEstudante,
    tempoPaciente,
    pctEstudante,
    pctPaciente,
    nuvemPalavras,
    pareceres,
  };
};
